import { useState } from "react"

import { motion } from "motion/react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize from "rehype-sanitize"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import "katex/dist/katex.min.css"

import type { ActivityMessage, ToolApproval } from "@/api/models"
import type { Schema } from "hast-util-sanitize"

import { ChevronDown, Sparkles } from "@/components/icons"
import { MemoryCandidate } from "@/components/MemoryCandidate/MemoryCandidate"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import styles from "@/features/chat/styles/answer.module.css"
import { chatMarkdownSanitizeSchema } from "@/features/chat/utils/markdown-sanitize-schema"
import { PlanUpgradeNotice } from "@/features/fair-use/components/plan-upgrade-notice"
import {
  extractPlanUpgradePayload,
  PLAN_UPGRADE_REQUIRED_CODE,
} from "@/features/fair-use/fair-use-plan-utils"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { cn } from "@/lib/utils"
import { stripMemoryCandidateTags } from "@/utils/prism-memory-utils"

import { CodeBlock } from "../code-block"
import {
  convertImageGeneratedResult,
  convertSearchResults,
  convertWebSearchResults,
  generateMarkdownContent,
  getActivityIcon,
} from "./activity-message.utils"
import { ApprovalDetailsContent } from "./approval-details-content"
import { ImageResult } from "./image-result"
import { SearchResults } from "./search-results"
import { ToolApprovalStatusBadge } from "./tool-approval-status-badge"
import { ToolsList } from "./tools-list"
import { WebScrapeResults } from "./web-scrape-results"
import { WebSearchResults } from "./web-search-results"
import { isModelRoutedActivity } from "../../utils/activity-message-utils"
import { findToolApprovalForActivity, isToolActivity } from "../../utils/tool-approval-lookup"

// Activity messages interpolate tool/search/RAG-derived fields (see
// generateMarkdownContent -> translateActivityMessage) into markdown, the
// same threat model as the main answer bubble (system-message.tsx) — needs
// the same sanitization. Typed as a tuple explicitly, same reason as
// system-message.tsx's chatSanitizePlugin: inlining widens it to a plain
// array and breaks react-markdown's Pluggable typing.
const activitySanitizePlugin: [typeof rehypeSanitize, Schema] = [
  rehypeSanitize,
  chatMarkdownSanitizeSchema,
]

// Explorer Auto model+effort routing decision (#2436). Same pill markup as
// the post-hoc chip in system-message.tsx — shown early, in Activity
// History, before the main model call streams anything.
function ModelRoutedPill({ activity }: { activity: ActivityMessage }) {
  const model = typeof activity.details?.model === "string" ? activity.details.model : ""
  const effort = typeof activity.details?.effort === "string" ? activity.details.effort : ""
  if (!model && !effort) return null
  return (
    <div className="bg-muted/60 text-muted-foreground inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium">
      <Sparkles className="size-3" />
      {model && <span>{model}</span>}
      {effort && (
        <>
          <span className="text-muted-foreground/40">·</span>
          <span className="capitalize">{effort}</span>
        </>
      )}
    </div>
  )
}

type ActivityMessageItemProps = {
  activity: ActivityMessage
  citationSection?: string
  activityIndex?: number
  demoMode?: boolean
  toolApprovals?: ToolApproval[]
}

export function ActivityMessageItem({
  activity,
  citationSection,
  activityIndex = 0,
  demoMode = false,
  toolApprovals,
}: ActivityMessageItemProps) {
  const { t } = useTranslation()
  const prismMemory = usePrismMemory()
  const [isExpanded, setIsExpanded] = useState(false)

  const activityType = activity.activity_type || "message"

  // Fair use round 2 (#3087): the image tool refuses at the plan's monthly
  // image entitlement with `activity_callback("plan_upgrade_required", …)`.
  // Rendered as the fair-use card rather than as another bold activity line —
  // the invariant is that a plan-locked feature never reaches the user as a
  // generic message. An unparseable frame still renders the card (the
  // component degrades to generic plan copy), never the raw payload.
  if (activityType === PLAN_UPGRADE_REQUIRED_CODE) {
    return (
      <motion.article
        animate={{ opacity: 1, y: 0, scale: 1 }}
        aria-label={`Activity: ${activityType.replace(/_/g, " ")}`}
        className="relative flex flex-col"
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.4, delay: Math.min(activityIndex * 0.03, 0.5) }}
      >
        <PlanUpgradeNotice payload={extractPlanUpgradePayload(activity.details)} />
      </motion.article>
    )
  }

  const content = generateMarkdownContent(activity, t)
  const markdown = prismMemory ? stripMemoryCandidateTags(content) : content
  const icon = getActivityIcon(activity)

  const staggerDelay = Math.min(activityIndex * 0.03, 0.5)

  const scrapedUrls =
    activityType === "web_scrape" && activity.details?.urls && Array.isArray(activity.details.urls)
      ? (activity.details.urls as string[])
      : null

  const memorySuggestion =
    !prismMemory &&
    activityType === "memory_saved" &&
    activity.details?.status === "suggested" &&
    typeof activity.details.text === "string" &&
    (activity.details.text as string).trim().length > 0
      ? (activity.details.text as string)
      : null

  const showResults =
    activityType === "web_search_results" ||
    activityType === "search_results" ||
    activityType === "image_generated" ||
    scrapedUrls !== null ||
    memorySuggestion !== null

  const matchingApproval = isToolActivity(activity)
    ? findToolApprovalForActivity(activity, toolApprovals)
    : null

  const hasExpandableDetails = matchingApproval !== null
  return (
    <motion.article
      animate={{ opacity: 1, y: 0, scale: 1 }}
      aria-label={`Activity: ${activityType.replace(/_/g, " ")}`}
      className="bg-muted/10 relative flex flex-col gap-4 rounded-xl p-4 backdrop-blur-sm"
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      transition={{
        duration: 0.4,
        delay: staggerDelay,
        ease: [0.23, 1, 0.32, 1],
      }}
    >
      {hasExpandableDetails ? (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <button className="group flex w-full items-start gap-3 text-left" type="button">
              <div
                aria-hidden="true"
                className="bg-muted/10 ring-primary/10 flex size-8 shrink-0 items-center justify-center rounded-lg ring-1"
              >
                {icon}
              </div>
              <div className="mt-1 min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      styles.answerText,
                      "prose prose-sm dark:prose-invert prose-h2:text-base prose-h2:font-semibold prose-h3:text-sm prose-h3:font-medium prose-p:my-0 prose-p:leading-relaxed prose-p:text-foreground/90"
                    )}
                  >
                    <ReactMarkdown
                      components={{ code: CodeBlock }}
                      rehypePlugins={[rehypeRaw, activitySanitizePlugin, rehypeKatex]}
                      remarkPlugins={[remarkGfm, remarkMath]}
                    >
                      {markdown}
                    </ReactMarkdown>
                  </div>
                  <ToolApprovalStatusBadge size="sm" status={matchingApproval.status} />
                  <span className="text-muted-foreground/60 ml-auto flex items-center gap-1.5 text-xs opacity-0 transition-opacity group-hover:opacity-100">
                    {isExpanded
                      ? t("activity_messages.hide_details", "Hide details")
                      : t("activity_messages.show_details", "Show details")}
                  </span>
                  <ChevronDown
                    className={cn(
                      "text-muted-foreground/50 size-4 shrink-0 transition-transform duration-200",
                      isExpanded && "rotate-180"
                    )}
                  />
                </div>
                {activity.activity_type === "tools_selected" &&
                  typeof activity.details?.tools === "string" && (
                    <ToolsList tools={activity.details.tools} />
                  )}
              </div>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ApprovalDetailsContent approval={matchingApproval} />
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <header className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="bg-muted/10 ring-primary/10 flex size-8 shrink-0 items-center justify-center rounded-lg ring-1"
          >
            {icon}
          </div>
          <div className="mt-1 min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
              {isModelRoutedActivity(activity) ? (
                <ModelRoutedPill activity={activity} />
              ) : (
                <div
                  className={cn(
                    styles.answerText,
                    "prose prose-sm dark:prose-invert prose-h2:text-base prose-h2:font-semibold prose-h3:text-sm prose-h3:font-medium prose-p:my-0 prose-p:leading-relaxed prose-p:text-foreground/90"
                  )}
                >
                  <ReactMarkdown
                    components={{ code: CodeBlock }}
                    rehypePlugins={[rehypeRaw, activitySanitizePlugin, rehypeKatex]}
                    remarkPlugins={[remarkGfm, remarkMath]}
                  >
                    {markdown}
                  </ReactMarkdown>
                </div>
              )}
            </div>
            {activityType === "tools_selected" && typeof activity.details?.tools === "string" && (
              <ToolsList tools={activity.details.tools} />
            )}
          </div>
        </header>
      )}

      {showResults && (
        <div className="relative min-w-0">
          <div className="from-border/0 via-border/20 to-border/0 absolute -inset-2 rounded-lg bg-linear-to-r" />
          <div className="relative min-w-0">
            {activityType === "web_search_results" && (
              <WebSearchResults activity={convertWebSearchResults(activity)} demoMode={demoMode} />
            )}
            {activityType === "search_results" && (
              <SearchResults
                activity={convertSearchResults(activity)}
                citationSection={citationSection}
                demoMode={demoMode}
              />
            )}
            {activityType === "image_generated" && (
              <ImageResult activity={convertImageGeneratedResult(activity)} demoMode={demoMode} />
            )}
            {scrapedUrls && <WebScrapeResults demoMode={demoMode} urls={scrapedUrls} />}
            {memorySuggestion && <MemoryCandidate text={memorySuggestion} />}
          </div>
        </div>
      )}
    </motion.article>
  )
}
